import React, { useState, useEffect, useRef } from "react";
import { MapPin, ChevronDown, Check } from "lucide-react";
import { BRAZIL_STATES_LIST, getCitiesForState } from "../../data/brazilCities";

interface CityAutocompleteProps {
  stateValue: string;
  onStateChange: (newState: string) => void;
  cityValue: string;
  onCityChange: (newCity: string) => void;
  stateLabel?: string;
  cityLabel?: string;
  darkTheme?: boolean;
}

export default function CityAutocomplete({
  stateValue,
  onStateChange,
  cityValue,
  onCityChange,
  stateLabel = "Estado (UF)",
  cityLabel = "Cidade",
  darkTheme = true,
}: CityAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Recalcular sugestões conforme o usuário digita
  useEffect(() => {
    const list = getCitiesForState(stateValue, cityValue);
    setSuggestions(list);
  }, [stateValue, cityValue]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectCity = (city: string) => {
    onCityChange(city);
    setIsOpen(false);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" ref={wrapperRef}>
      {/* SELETOR DE ESTADO (UF) */}
      <div className="sm:col-span-1">
        <label className={`block text-xs font-bold mb-1 ${darkTheme ? "text-emerald-300" : "text-slate-700"}`}>
          {stateLabel}:
        </label>
        <div className="relative">
          <select
            value={stateValue}
            onChange={(e) => {
              onStateChange(e.target.value);
              setIsOpen(true);
            }}
            className={`w-full px-3 py-2 border rounded-xl text-xs font-bold focus:outline-none cursor-pointer appearance-none ${
              darkTheme
                ? "bg-zinc-950 border-zinc-800 text-white focus:border-emerald-500"
                : "bg-slate-50 border-slate-200 text-slate-900 focus:ring-2 focus:ring-emerald-500"
            }`}
          >
            <option value="">Selecione UF</option>
            {BRAZIL_STATES_LIST.map((st) => (
              <option key={st.uf} value={st.uf}>
                {st.uf} - {st.name}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className={`absolute right-3 top-3 pointer-events-none ${darkTheme ? "text-zinc-400" : "text-slate-400"}`} />
        </div>
      </div>

      {/* CAMPO DE AUTOCOMPLETE DE CIDADE */}
      <div className="sm:col-span-2 relative">
        <label className={`block text-xs font-bold mb-1 ${darkTheme ? "text-emerald-300" : "text-slate-700"}`}>
          {cityLabel}:
        </label>
        <div className="relative">
          <MapPin size={16} className={`absolute left-3 top-2.5 ${darkTheme ? "text-amber-400" : "text-emerald-600"}`} />
          <input
            type="text"
            placeholder="Digite sua cidade (ex: São Paulo, Campinas...)"
            value={cityValue}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => {
              onCityChange(e.target.value);
              setIsOpen(true);
            }}
            className={`w-full pl-9 pr-3 py-2 border rounded-xl text-xs font-medium focus:outline-none ${
              darkTheme
                ? "bg-zinc-950 border-zinc-800 text-white focus:border-emerald-500"
                : "bg-slate-50 border-slate-200 text-slate-900 focus:ring-2 focus:ring-emerald-500"
            }`}
          />
        </div>

        {/* DROPDOWN DE SUGESTÕES DE CIDADES DAQUELE ESTADO */}
        {isOpen && (
          <div
            className={`absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-2xl border shadow-2xl py-1 custom-scrollbar ${
              darkTheme ? "bg-zinc-900 border-zinc-800 text-white" : "bg-white border-slate-200 text-slate-900"
            }`}
          >
            {suggestions.length > 0 ? (
              suggestions.map((c) => {
                const isMatch = c.toLowerCase() === cityValue.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => handleSelectCity(c)}
                    className={`w-full text-left px-3.5 py-2 text-xs transition cursor-pointer flex items-center justify-between ${
                      isMatch
                        ? darkTheme
                          ? "bg-amber-400/20 text-amber-300 font-bold"
                          : "bg-emerald-50 text-emerald-800 font-bold"
                        : darkTheme
                        ? "hover:bg-zinc-800 text-zinc-200"
                        : "hover:bg-slate-100 text-slate-800"
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} className={darkTheme ? "text-emerald-400" : "text-emerald-600"} />
                      {c}
                    </span>
                    {isMatch && <Check size={12} className="text-amber-400" />}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-[11px] text-zinc-500 text-center">
                {stateValue
                  ? `Nenhuma cidade pré-cadastrada com "${cityValue}" em ${stateValue}. Você pode continuar digitando.`
                  : "Selecione primeiro o Estado (UF) para ver as cidades."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
